import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Autocomplete,
  IconButton,
  Paper,
  Box,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Swal from "sweetalert2";
import api from "../../api";

const WalletHistory = () => {
  const [storedUser, setStoredUser] = useState({});
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("school_id");
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({
    session: null
  });
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [txnType, setTxnType] = useState("");
  const [searchText, setSearchText] = useState("");

  const [selectedStaff, setSelectedStaff] = useState("");
  const [AllStaff, setStaff] = useState([]);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const exportToExcel = () => {
    const exportData = data.map((row, index) => ({
      ID: index + 1,
      "User Name": row.user_name,
      "Txn Type": row.type === "C" ? "Credit" : "Debit",
      Amount: row.amount,
      "Before Balance": row.before_bal,
      "Updated Balance": row.update_bal,
      "Txn ID": row.txn_id,
      Date: row.created_at,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(fileData, "transactions.xlsx");
  };

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // 🧩 Init
  useEffect(() => {
    fetchSessions();
  }, []);

  // 🧩 Fetch Sessions
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list", {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status !== false) {
        const data = res.data.data || [];
        data.sort((a, b) => (a.status === "active" ? -1 : 1));
        setSessions(data);

        // Auto-select active session
        if (!filters.session && data.length) {
          const active = data.find((s) => s.status === "active");
          setFilters((p) => ({ ...p, session: active || data[0] }));
        }
      }
    } catch {
      Swal.fire("Error", "Failed to fetch sessions", "error");
    }
  };

  // ✅ Multi-school Apply Filter
  const applyFilter = async () => {
    const formatDate = (date) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0]; // 'YYYY-MM-DD'
    };

    const filter = {
      user_id: localStorage.getItem("id"),
      role: localStorage.getItem("role"),
      from_date: formatDate(fromDate),
      to_date: formatDate(toDate),
      txn_type: txnType || "",
      search: searchText.trim() || "",
      staff_id: selectedStaff || "",
      academic_year_id: filters.session?.id,
    };

    try {
      const response = await api.post("/wallet-history", filter, {
        headers: {
          "Content-Type": "application/json",
          "X-School-Id": localStorage.getItem("school_id"), // ✅ Always send school_id
        },
      });

      if (response.data.status) {
        setData(response.data.data);
      } else {
        setData([]); // fallback empty
        console.error("API error:", response.data.message);
      }
    } catch (error) {
      console.error("Network/API error:", error);
      setData([]);
    }
  };

  // ✅ Store logged user details
  useEffect(() => {
    const id = localStorage.getItem("id");
    if (id) {
      const user = {
        id,
        user_id: localStorage.getItem("user_id"),
        name: localStorage.getItem("name"),
        mobile: localStorage.getItem("mobile"),
        role: localStorage.getItem("role"),
      };
      setStoredUser(user);
    }
  }, []);

  // ✅ Load Staff List (multi-school)
  const loadStaff = () => {
    api
      .get("/get-all-staff", {
        headers: { "X-School-Id": localStorage.getItem("school_id") }, // ✅ Send school_id
      })
      .then((res) => {
        if (res.data.status) {
          setStaff(res.data.data);
        } else {
          setStaff([]);
        }
      })
      .catch((error) => console.error("Error fetching staff:", error));
  };

  useEffect(() => {
    loadStaff();
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", marginTop: "0px" }}>
      <Box sx={{ display: "flex", bgcolor: "#f9fafc" }}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 2,
            mt: 0,
            transition: "margin 0.3s",
          }}
        >
          {/* Filter Card */}
          <Card
            sx={{
              background: "linear-gradient(135deg, #e0f7fa, #e1bee7)",
              boxShadow: 4,
              borderRadius: 4,
              mb: 4,
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#4a148c", fontWeight: 700 }}>
              🔍 Filter Transactions
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Autocomplete
                    options={sessions}
                    getOptionLabel={(opt) =>
                      `${opt.session_name || ""}${opt.status ? ` (${opt.status})` : ""}`
                    }
                    value={filters.session}
                    onChange={(_, v) => setFilters((p) => ({ ...p, session: v }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Session" fullWidth />
                    )}
                    clearOnEscape
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                {/* ✅ Super admin only: staff filter */}
                {storedUser.role === "super_admin" && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={AllStaff}
                      getOptionLabel={(option) => `${option.name} ₹${option.balance}`}
                      value={AllStaff.find((staff) => staff.id === selectedStaff) || null}
                      onChange={(event, newValue) => {
                        setSelectedStaff(newValue ? newValue.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Staff"
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {selectedStaff && (
                                  <IconButton
                                    onClick={() => setSelectedStaff("")}
                                    edge="end"
                                    size="small"
                                    aria-label="clear"
                                    title="Clear selection"
                                  />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    label="Transaction Type"
                    fullWidth
                    value={txnType}
                    onChange={(e) => setTxnType(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="C">Credit</MenuItem>
                    <MenuItem value="D">Debit</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Search by Name / Txn ID"
                    fullWidth
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      mt: 1,
                      background: "linear-gradient(90deg,#7b1fa2,#512da8)",
                      color: "#fff",
                      fontWeight: "bold",
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      textTransform: "none",
                    }}
                    onClick={applyFilter}
                  >
                    Apply Filter
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Card>

          {/* Data Table Card */}
          <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
            <CardContent>
              <TableContainer>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1a237e" }}>
                    📊 Transaction History
                  </Typography>
                  <Button variant="outlined" onClick={exportToExcel}>
                    Export to Excel
                  </Button>
                </Box>

                <Table>
                  <TableHead sx={{ backgroundColor: "#ede7f6" }}>
                    <TableRow>
                      {[
                        "ID",
                        "User Name",
                        "Txn Type",
                        "Amount",
                        "Before Balance",
                        "Updated Balance",
                        "Txn ID",
                        "Date",
                      ].map((header) => (
                        <TableCell key={header} sx={{ fontWeight: "bold" }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.length > 0 ? (
                      data
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, index) => (
                          <TableRow key={row.id}>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{row.user_name}</TableCell>
                            <TableCell
                              sx={{
                                color: row.type === "C" ? "green" : "red",
                                fontWeight: 600,
                              }}
                            >
                              {row.type === "C" ? "Credit" : "Debit"}
                            </TableCell>
                            <TableCell>₹{Number(row.amount).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{Number(row.before_bal).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{Number(row.update_bal).toLocaleString("en-IN")}</TableCell>
                            <TableCell>{row.txn_id}</TableCell>
                            <TableCell>{row.created_at}</TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No data found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={data?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default WalletHistory;
